/**
 * 리니지 - 시세
 */
import { Fragment } from 'react';
import { getServerId, setStrongTag, getPlatform, getPriceNumToStr, numberWithCommas } from '@utils/index';
import { NETWORK, PLATFORM, SEARCH_PARAMS, STATIC_DOMAIN } from '@constants/index';
import type { l10nType, ITrade } from '@type/index';

const TRADE_TYPE = {
  SELL: 'sell',
  BUY: 'buy',
};

const TRADE_CATEGORY = {
  ALL: 'all',
  NORMAL: 'normal',
  BLESS: 'bless',
  CURSE: 'curse',
  NOT_IDENT: 'notIdent',
};

const TRADE_ITEM_STATUS = {
  [TRADE_CATEGORY.ALL]: 'ALL',
  [TRADE_CATEGORY.NORMAL]: 'NORMAL',
  [TRADE_CATEGORY.BLESS]: 'BLESS',
  [TRADE_CATEGORY.CURSE]: 'CURSE',
  [TRADE_CATEGORY.NOT_IDENT]: 'NOT_IDENT',
};

const TRADE_CATEGORY_INDEX = {
  [TRADE_CATEGORY.ALL]: 0,
  [TRADE_CATEGORY.NORMAL]: 1,
  [TRADE_CATEGORY.BLESS]: 2,
  [TRADE_CATEGORY.CURSE]: 3,
  [TRADE_CATEGORY.NOT_IDENT]: 4,
};

const getPriceStr = (low: number, high: number) => {
  let result = '';

  if (typeof low == 'number' && low > 0 && typeof high === 'number' && high > 0) {
    result = low !== high ? `${getPriceNumToStr(low)?.trim()} - ${getPriceNumToStr(high)?.trim()}` : `${getPriceNumToStr(low)}`;
  }

  return result;
};

export const getTradeMoreUrl = (keyword: string) => {
  const params = {
    query: keyword || '',
    serverId: getServerId(),
  };

  return `/world/item/search?${new URLSearchParams(params)?.toString()}`;
};

export const setTradeItem = (item: ITrade, keyword: string, l10n: l10nType): ISetTrade | null => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const PET_NECKLACE_ID = 25668;
  const isPetNecklace = item?.itemId === PET_NECKLACE_ID;
  const sellData = Object.values(TRADE_CATEGORY)?.map((key) => {
    const index = TRADE_CATEGORY_INDEX[key];
    return {
      category: key,
      title: l10n?.template[`trade_${key}`],
      low: [item?.sellLow, item?.normalSellLow, item?.blessSellLow, item?.curseSellLow, item?.notIdentSellLow][index],
      high: [item?.sellHigh, item?.normalSellHigh, item?.blessSellHigh, item?.curseSellHigh, item?.notIdentSellHigh][index],
      count: [item?.sellCount, item?.normalSellCount, item?.blessSellCount, item?.curseSellCount, item?.notIdentSellCount][index],
      hiddenCount: [
        item?.sellHiddenCount,
        item?.normalSellHiddenCount,
        item?.blessSellHiddenCount,
        item?.curseSellHiddenCount,
        item?.notIdentSellHiddenCount,
      ][index],
      hiddenCountTotal: item?.sellHiddenCount,
    };
  });

  const buyData = Object.values(TRADE_CATEGORY)?.map((key) => {
    const index = TRADE_CATEGORY_INDEX[key];
    return {
      category: key,
      title: l10n?.template[`trade_${key}`],
      low: [item?.buyLow, item?.normalBuyLow, item?.blessBuyLow, item?.curseBuyLow, item?.notIdentBuyLow][index],
      high: [item?.buyHigh, item?.normalBuyHigh, item?.blessBuyHigh, item?.curseBuyHigh, item?.notIdentBuyHigh][index],
      count: [item?.buyCount, item?.normalBuyCount, item?.blessBuyCount, item?.curseBuyCount, item?.notIdentBuyCount][index],
    };
  });

  const isIngame = getPlatform() === PLATFORM.INGAME;
  const path = isIngame ? '/ingame' : '/world';
  let sellPriceUrlParams = {
    serverId: `${item?.serverId}`,
    tradeType: TRADE_TYPE.SELL.toUpperCase(),
    itemId: `${item?.itemId}`,
    enchant: `${item?.enchant}`,
  };

  let buyPriceUrlParams = {
    serverId: `${item?.serverId}`,
    tradeType: TRADE_TYPE.BUY.toUpperCase(),
    itemId: `${item?.itemId}`,
    enchant: `${item?.enchant}`,
  };

  let itemNameHtml = setStrongTag(item?.itemName, keyword);
  let altItemName = '';

  if (isPetNecklace) {
    itemNameHtml = item?.displayName;
    altItemName = item?.displayName;
    sellPriceUrlParams = { ...sellPriceUrlParams, info: `${item?.info}` };
    buyPriceUrlParams = { ...buyPriceUrlParams, info: `${item?.info}` };
  } else {
    itemNameHtml = item?.enchant > 0 ? `+${item?.enchant} ${itemNameHtml}` : itemNameHtml;
    altItemName = item?.enchant > 0 ? `+${item?.enchant} ${item?.itemName}` : item?.itemName;
  }

  return item && typeof item === 'object'
    ? {
        ...item,
        noItemImageUrl: `${STATIC_DOMAIN[NETWORK.LIVE]}/lineage/v1/img/world/item/noimg.gif`,
        itemNameHtml,
        sellData,
        buyData,
        sellPriceUrl: `${path}/item/detail?${new URLSearchParams(sellPriceUrlParams)?.toString()}`,
        buyPriceUrl: `${path}/item/detail?${new URLSearchParams(buyPriceUrlParams)?.toString()}`,
      }
    : null;
};

const TradeItem = ({ type, data, url, l10n }: { type: (typeof TRADE_TYPE)[keyof typeof TRADE_TYPE]; data: any; url: string; l10n: l10nType }) => {
  if (!data || typeof data !== 'object') {
    return <Fragment />;
  }

  const noDataList = [];
  const dataList = [];

  for (let key in TRADE_CATEGORY_INDEX) {
    const index = TRADE_CATEGORY_INDEX[key];
    const { category, title, low, high, count } = data[index];
    const priceStr = getPriceStr(low, high);

    if (priceStr && count > 0) {
      dataList?.push({
        title,
        priceStr,
        count: numberWithCommas(count),
        priceUrl: `${url}&itemStatus=${TRADE_ITEM_STATUS[category]}`,
      });
    } else {
      noDataList?.push(category);
    }
  }

  return (
    <div className={`search-item-flex ${type}`}>
      <h4 className="search-item-name">{l10n?.template[`trade_${type}`]}</h4>
      <ul className="search-item-info">
        {Array.isArray(dataList) &&
          dataList?.length > 0 &&
          dataList?.map((v) => (
            <li key={v?.priceUrl}>
              <a href={v?.priceUrl}>
                <span className="category">{v?.title}</span>
                <span className="price">{v?.priceStr}</span>
                <span className="count">({v?.count})</span>
              </a>
            </li>
          ))}
        {Array.isArray(noDataList) && noDataList?.length > 0 && (
          <li key="trade_nodata" className="nodata">
            {noDataList?.map((v) => (
              <span key={v} className="category">
                {l10n?.template[`trade_${v}`]}
              </span>
            ))}{' '}
            {l10n?.template?.trade_nodata}
          </li>
        )}
        {type === TRADE_TYPE.SELL ? (
          <li key="trade_hiddenCountTotal" className="hidden">
            {l10n?.template?.trade_hidden} {data[TRADE_CATEGORY_INDEX[TRADE_CATEGORY.ALL]]?.hiddenCountTotal}
            {l10n?.template?.count2}
          </li>
        ) : (
          <Fragment />
        )}
      </ul>
    </div>
  );
};

const TradeArticle = ({ item, searchParams, l10n }: { item: ITrade; searchParams: URLSearchParams; l10n: l10nType }) => {
  const keyword = searchParams?.get(SEARCH_PARAMS.KEYWORD) || '';
  const data = setTradeItem(item, keyword, l10n);

  if (!data) {
    return <Fragment />;
  }

  const { serverName, thumbnail, itemNameHtml, sellPriceUrl, buyPriceUrl, sellData, buyData } = data;
  return (
    <div className="search-item-content">
      <div className="search-item-article main column column-1">
        <div className="search-item-flex">
          <h3 className="search-item-name">{serverName}</h3>
          <div className="search-item-flex flex-row">
            <div className="search-item-thumbnail">
              <figure style={{ backgroundImage: `url(${thumbnail})` }}></figure>
            </div>
            <div className="search-item-title">
              <a href={sellPriceUrl} dangerouslySetInnerHTML={{ __html: itemNameHtml }}></a>
            </div>
          </div>
        </div>
      </div>
      <div className="search-item-article detail column column-2">
        <TradeItem type={TRADE_TYPE.SELL} data={sellData} url={sellPriceUrl} l10n={l10n} />
        <TradeItem type={TRADE_TYPE.BUY} data={buyData} url={buyPriceUrl} l10n={l10n} />
      </div>
    </div>
  );
};

export default TradeArticle;
